<template>
  <template v-if="modes.includes('list')">
    <div class="tags-list-header">
      <div class="tags-list-title ellipsis">
        {{ listTitle }}
      </div>
      <div v-if="modes.includes('dropdown')" class="tags-list-button">
        <rbz-form-button
          class="switch-tag-mode-button"
          v-b-tooltip="t('tags.selection.addTag')"
          variant="tertiary"
          icon="IconAdd"
          data-qa="switch-tag-mode-button"
          :icon-props="{ color: 'shuttle-gray' }"
          :disabled="disabled"
          @button-click="switchMode('dropdown')"
        />
      </div>
    </div>
    <div v-if="listHeaderDivider" class="list-header-divider">
      <layout-divider-line orientation="vertical" color="light-gray" />
    </div>
    <layout-divider-line color="light-gray" />
  </template>
  <template v-if="currentMode === 'list'">
    <div class="tags-list">
      <template v-if="!value?.length">
        <div class="tag-display no-tags-message">
          {{ t('tags.selection.noTags') }}
        </div>
        <layout-divider-line color="light-gray" />
      </template>
      <template v-for="tag in value" :key="tag">
        <div class="tag-display">
          <rbz-custom-tag
            :value="tag"
            :remove-option="true"
            :variant="tagVariant ? tagVariant(tag) : 'fill'"
            :tooltip-text="tagTooltip ? tagTooltip(tag) : tag"
            @remove="removeTag(tag)"
            :disabled="disabled"
          />
        </div>
        <layout-divider-line color="light-gray" />
      </template>
    </div>
  </template>
  <template v-if="currentMode === 'dropdown'">
    <div ref="dropdownWrapperRef" :class="{ 'list-dropdown': modes.includes('list') }">
      <rbz-dropdown
        :items="availableTags"
        type="multi-select"
        :value="value"
        @update:value="selectedTagsChanged($event as string[])"
        @search="addTag($event)"
        :disabled="disabled"
        :filter-force-display="true"
        filter-variant="search-trigger"
        ref="dropdownRef"
        :label="label"
        classes="tags-selection"
        data-qa="tags-selection"
      >
        <template #headerCustomTagOverride="props">
          <rbz-custom-tag
            v-for="item in props.checkedItems"
            :key="item[0]"
            :value="item[1].toString()"
            :remove-option="true"
            :disabled="disabled"
            :tooltip-text="tagTooltip ? tagTooltip(item[1].toString()) : item[1].toString()"
            @remove="removeTag(item[1].toString())"
          />
        </template>
      </rbz-dropdown>
    </div>
    <layout-divider-line v-if="modes.includes('list')" color="light-gray" />
  </template>
</template>

<script setup lang="ts">
import RbzDropdown from '@/components/RbzDropdown.vue';
import _ from 'lodash';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ListType, useTagsStore } from '@/store/TagsStore';
import RbzFormButton from '@/components/RbzFormButton.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import RbzCustomTag from '@/components/RbzCustomTag.vue';
import { TagVariant } from '@/types';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

type Mode = 'list' | 'dropdown';

interface TagsSelection {
  value: string[];
  listType?: ListType;
  readOnly?: boolean;
  label?: string;
  modes?: Mode[];
  currentMode?: Mode;
  listTitle?: string;
  listHeaderDivider?: boolean;
  disabled?: boolean;
  tagVariant?: (tag: string) => TagVariant;
  tagTooltip?: (tag: string) => string;
}

const props = withDefaults(defineProps<TagsSelection>(), {
  listType: 'all',
  readOnly: false,
  modes: () => ['dropdown'],
  currentMode: 'dropdown',
});

interface OptionEmit {
  (e: 'update:value', value: string[] | string): void;
}

const emit = defineEmits<OptionEmit>();

const tagsStore = useTagsStore();
const availableTags = ref([]);
const isLoading = ref(true);
const dropdownWrapperRef = ref<HTMLElement>(null);
const dropdownRef = ref(null);
const currentMode = ref(props.currentMode);
watch(
  () => props.currentMode,
  async () => {
    currentMode.value = props.currentMode;
  }
);
watch(
  () => props.listType,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      loadAvailableTags();
      tagsStore.removeTagsChangeListener(tagsListChanged, oldValue);
      tagsStore.addTagsChangeListener(tagsListChanged, newValue);
    }
  }
);

const loadAvailableTags = async () => {
  isLoading.value = true;
  const tags = await tagsStore.getTags(props.listType);
  availableTags.value = _.map(tags, (tag: string) => {
    return [tag, tag];
  });
  isLoading.value = false;
};

loadAvailableTags();

const selectedTagsChanged = (newTagsList: string[]) => {
  newTagsList = _.map(newTagsList, (tag: string) => {
    return tag.trim();
  });
  emit('update:value', newTagsList);
};

const removeTag = (tagToRemove: string) => {
  const newTagsList = _.filter(props.value, (tag: string) => {
    return tag !== tagToRemove;
  });
  emit('update:value', newTagsList);
};

const addTag = (newTag: string) => {
  if (props.readOnly) {
    return;
  }
  const normalizedTag = tagsStore.normalizeTag(newTag);
  if (!normalizedTag || normalizedTag.length < 3) {
    return;
  }
  const tagExists = _.some(props.value, (tag: string) => {
    return tag === normalizedTag;
  });
  if (tagExists) {
    return;
  }
  const isTagAvailable = _.some(availableTags.value, (availableTag: [string, string]) => {
    return availableTag[0] === normalizedTag;
  });
  if (!isTagAvailable) {
    tagsStore.addTag(normalizedTag, props.listType);
  }
  const oldTagsList = _.cloneDeep(props.value);
  const newTagsList = oldTagsList?.length ? oldTagsList : [];
  newTagsList.push(normalizedTag);
  emit('update:value', newTagsList);
};

const switchMode = (newMode: Mode) => {
  currentMode.value = newMode;
  if (newMode === 'dropdown') {
    nextTick(() => {
      dropdownRef.value.toggleDropdown();
    });
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (!dropdownWrapperRef.value?.contains(event.target as Node)) {
    if (props.modes.includes('list')) {
      currentMode.value = 'list';
    }
  }
};

const tagsListChanged = (newTags: string[]) => {
  availableTags.value = _.map(newTags, (tag: string) => {
    return [tag, tag];
  });
};

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
  tagsStore.addTagsChangeListener(tagsListChanged, props.listType);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  tagsStore.removeTagsChangeListener(tagsListChanged, props.listType);
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

$tag-line-height: 48px;

.tags-list-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 12px;
}

.list-header-divider {
  height: 32px;
  position: absolute;
  right: 0;
  top: 8px;
}

.tags-list-title {
  color: map.get(colors.$colors, 'bright-gray');
}

:deep(.tags-list-button .button-icon) {
  color: map.get(colors.$colors, 'shuttle-gray');
}

.list-dropdown {
  height: $tag-line-height;
  padding: 4px;
}

.tag-display {
  align-items: center;
  display: flex;
  height: $tag-line-height;
  padding: 12px;
}

.no-tags-message {
  color: map.get(colors.$colors, 'shuttle-gray');
}

:deep(.switch-tag-mode-button) {
  .rbz-button {
    height: 24px;
    padding: 0;
    width: 24px;

    &.icon-only {
      padding: 0;
    }
  }
}
</style>
